import { useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { fetchInstitutionDonorOrders } from '@/lib/institution-donors'
import type { InstitutionDonorOrder } from '@/types/institution-donor'
import { DonorRouletteNumbers } from './DonorRouletteNumbers'
import { DonorOrderCard } from './DonorOrderCard'
import { DonorOrderCancelModal } from './DonorOrderCancelModal'
import styles from './DonorLuckyNumbersSection.module.css'

type Props = {
  donorId: number
  token?: string | null
  total: number
  canCancel?: boolean
  onOrderCanceled?: () => void
}

export function DonorLuckyNumbersSection({
  donorId,
  token,
  total,
  canCancel = false,
  onOrderCanceled,
}: Props) {
  const [orders, setOrders] = useState<InstitutionDonorOrder[]>([])
  const [page, setPage] = useState(0)
  const [lastPage, setLastPage] = useState(1)
  const [orderTotal, setOrderTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [canceling, setCanceling] = useState<InstitutionDonorOrder | null>(null)
  const [notice, setNotice] = useState('')
  const generation = useRef(0)

  useEffect(() => {
    const current = ++generation.current
    setOrders([])
    setPage(0)
    setLastPage(1)
    setLoading(true)
    setError(false)
    setCanceling(null)
    setNotice('')
    void fetchInstitutionDonorOrders(donorId, 1, token)
      .then((result) => {
        if (current !== generation.current) return
        setOrders(result.orders)
        setPage(result.pagination.current_page)
        setLastPage(result.pagination.last_page)
        setOrderTotal(result.pagination.total)
      })
      .catch(() => {
        if (current === generation.current) setError(true)
      })
      .finally(() => {
        if (current === generation.current) setLoading(false)
      })
    return () => {
      generation.current = current + 1
    }
  }, [donorId, token])

  const loadMore = async () => {
    const current = generation.current
    setLoading(true)
    setError(false)
    try {
      const result = await fetchInstitutionDonorOrders(donorId, page + 1, token)
      if (current !== generation.current) return
      setOrders((existing) => [
        ...new Map(
          [...existing, ...result.orders].map((order) => [order.id, order])
        ).values(),
      ])
      setPage(result.pagination.current_page)
      setLastPage(result.pagination.last_page)
      setOrderTotal(result.pagination.total)
    } catch {
      if (current === generation.current) setError(true)
    } finally {
      if (current === generation.current) setLoading(false)
    }
  }

  const campaigns = new Map<number, { title: string; orders: InstitutionDonorOrder[] }>()
  orders.forEach((order) => {
    const group = campaigns.get(order.campaign_id) ?? {
      title: order.campaign_title ?? `Campanha #${order.campaign_id}`,
      orders: [],
    }
    group.orders.push(order)
    campaigns.set(order.campaign_id, group)
  })

  return (
    <section className={styles.section} aria-labelledby="donor-numbers-title">
      <div className={styles.headingRow}>
        <div>
          <span className={styles.eyebrow}>Participação</span>
          <h2 id="donor-numbers-title" className={styles.title}>
            Números da sorte
          </h2>
          <p className={styles.subtitle}>
            Compras por campanha. Expanda um pedido para consultar seus números.
          </p>
        </div>
        <span className={styles.count}>
          {total} {total === 1 ? 'número ativo' : 'números ativos'}
        </span>
      </div>
      {notice && (
        <p className={styles.notice} role="status">
          {notice}
        </p>
      )}
      <div className={styles.campaignList}>
        {[...campaigns].map(([id, campaign]) => (
          <details className={styles.campaignGroup} key={id} open>
            <summary className={styles.campaignHeading}>
              <Icon icon="mdi:chevron-right" className={styles.chevron} aria-hidden />
              <Icon icon="mdi:ticket-confirmation-outline" aria-hidden />
              <strong>{campaign.title}</strong>
            </summary>
            <div className={styles.orderList}>
              {campaign.orders.map((order) => (
                <DonorOrderCard
                  key={`${order.id}-${order.status}`}
                  order={order}
                  donorId={donorId}
                  token={token}
                  canCancel={canCancel}
                  onCancel={() => setCanceling(order)}
                />
              ))}
            </div>
          </details>
        ))}
      </div>
      {!orders.length && !loading && !error && (
        <p className={styles.empty}>Nenhum pedido pago ou cancelado encontrado.</p>
      )}
      {loading && (
        <p className={styles.status} role="status">
          Carregando pedidos…
        </p>
      )}
      {error && (
        <p className={styles.error} role="alert">
          Não foi possível carregar os pedidos. Tente novamente.
        </p>
      )}
      {(error || (!loading && page < lastPage)) && (
        <button
          type="button"
          className={styles.moreButton}
          onClick={() => void loadMore()}
          disabled={loading}
        >
          {error
            ? 'Tentar novamente'
            : `Ver mais pedidos (${orders.length} de ${orderTotal})`}
        </button>
      )}
      <DonorRouletteNumbers key={donorId} donorId={donorId} token={token} />
      {canceling && (
        <DonorOrderCancelModal
          order={canceling}
          donorId={donorId}
          token={token}
          onClose={() => setCanceling(null)}
          onCanceled={(order) => {
            setOrders((existing) =>
              existing.map((item) => (item.id === order.id ? order : item))
            )
            setCanceling(null)
            setNotice(
              `Pedido #${order.id} cancelado. Os números foram devolvidos ao sistema. Nenhum estorno financeiro foi realizado.`
            )
            onOrderCanceled?.()
          }}
        />
      )}
    </section>
  )
}
